/**
 * AboutView Component
 *
 * Plain-language overview of the four-tier GridTwinX architecture.
 */

import React from 'react';
import DataBadge from './DataBadge';

const AboutView: React.FC = () => {
  return (
    <div className="h-full w-full bg-gray-900 p-4 overflow-y-auto">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="bg-gray-800 rounded-lg p-5 border border-gray-700">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-white">GridTwinX Architecture</h2>
            <DataBadge tier="SYSTEM" />
          </div>
          <p className="text-sm text-gray-400 mt-2">
            GridTwinX is a digital twin for New Zealand's power system. It couples solver-grade
            physics with live operational feeds to test energy futures, data-centre demand, and
            fairness outcomes.
          </p>
        </header>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">Tier I: Data Kernel</h3>
            <p className="text-sm text-gray-400">
              Event ingestion, measurement mapping, and deterministic replay. This tier keeps the
              system grounded in telemetry and topology changes.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-300">
              <span className="px-2 py-1 rounded bg-gray-700">Telemetry</span>
              <span className="px-2 py-1 rounded bg-gray-700">Breaker Events</span>
              <span className="px-2 py-1 rounded bg-gray-700">Replay Logs</span>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">Tier II: Physics Engine</h3>
            <p className="text-sm text-gray-400">
              Power-flow and chrono solvers coupled with data-centre load and flexibility modules.
              This is where the grid is simulated under stress.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-300">
              <span className="px-2 py-1 rounded bg-gray-700">AC/DC Power Flow</span>
              <span className="px-2 py-1 rounded bg-gray-700">Chrono Runs</span>
              <span className="px-2 py-1 rounded bg-gray-700">Flex Loads</span>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">Tier III: Scenario Intelligence</h3>
            <p className="text-sm text-gray-400">
              EDGS scenarios, Monte Carlo sampling, and strategy presets to compare pathways and
              fairness trade-offs.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-300">
              <span className="px-2 py-1 rounded bg-gray-700">EDGS</span>
              <span className="px-2 py-1 rounded bg-gray-700">Monte Carlo</span>
              <span className="px-2 py-1 rounded bg-gray-700">Strategies</span>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-2">Tier IV: Governance & Assurance</h3>
            <p className="text-sm text-gray-400">
              Fairness metrics, audit trails, and verification checks that make results defensible.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-300">
              <span className="px-2 py-1 rounded bg-gray-700">Fairness KPIs</span>
              <span className="px-2 py-1 rounded bg-gray-700">Run Verification</span>
              <span className="px-2 py-1 rounded bg-gray-700">Evidence Logs</span>
            </div>
          </div>
        </section>

        <section className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-2">Data Provenance Legend</h3>
          <p className="text-sm text-gray-400">
            Every panel carries a badge so you know whether you are seeing live feeds, modelled
            outputs, or scenario data.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <DataBadge tier="LIVE" />
            <DataBadge tier="MODELLED" />
            <DataBadge tier="SCENARIO" />
            <DataBadge tier="SYSTEM" />
            <DataBadge tier="BACKUP" />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutView;
